import { Skeleton } from "@/components/kit";
import type { CustomerGeoResultDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { ReportMobileCard, ReportStat } from "./ReportProductLink";

type CustomerGeoTableProps = {
  result?: CustomerGeoResultDTO;
  isLoading?: boolean;
  onProvinceSelect?: (province: string) => void;
  selectedProvince?: string;
  onClearProvince?: () => void;
};

function GeoMobileCard({
  row,
  onSelect,
}: {
  row: CustomerGeoResultDTO["data"][number];
  onSelect?: () => void;
}) {
  return (
    <ReportMobileCard>
      <button
        type="button"
        className="w-full text-left"
        onClick={onSelect}
        disabled={!onSelect}
      >
        <span className="font-medium">{row.label}</span>
      </button>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat label="ລູກຄ້າ" value={row.customerCount} />
        <ReportStat label="ອໍເດີ້" value={row.orderCount} />
        <ReportStat
          label="ຍອດ"
          value={formatLak(row.revenue)}
          className="col-span-2"
        />
      </dl>
    </ReportMobileCard>
  );
}

export function CustomerGeoTable({
  result,
  isLoading,
  onProvinceSelect,
  selectedProvince,
  onClearProvince,
}: CustomerGeoTableProps) {
  const rows = result?.data ?? [];
  const isDistrict = result?.level === "district";

  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-2xl" />;
  }

  return (
    <section className="brand-card overflow-hidden">
      <div className="flex flex-col gap-1 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-lg">
            {isDistrict ? "ຕາມເມືອງ" : "ຕາມແຂວງ"}
          </h2>
          {selectedProvince && onClearProvince ? (
            <button
              type="button"
              className="rounded-full border border-border px-2.5 py-0.5 text-muted-foreground text-xs hover:text-foreground"
              onClick={onClearProvince}
            >
              {selectedProvince} ×
            </button>
          ) : null}
        </div>
        <p className="text-muted-foreground text-sm">
          {isDistrict
            ? `ແຂວງ ${result?.province ?? selectedProvince ?? ""} — ຍອດຂາຍຢືນຢັນແຍກຕາມເມືອງ`
            : "ຍອດຂາຍຢືນຢັນແຍກຕາມແຂວງ — ຄລິກແຖວເພື່ອເບິ່ງເມືອງ"}
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="border-secondary/15 border-t p-8 text-center text-muted-foreground text-sm">
          ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 border-secondary/15 border-t p-4 md:hidden">
            {rows.map((row) => (
              <GeoMobileCard
                key={row.label}
                row={row}
                onSelect={
                  !isDistrict && onProvinceSelect && row.label !== "ບໍ່ລະບຸ"
                    ? () => onProvinceSelect(row.label)
                    : undefined
                }
              />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs">
                <tr>
                  <th className="px-4 py-3 font-medium">
                    {isDistrict ? "ເມືອງ" : "ແຂວງ"}
                  </th>
                  <th className="px-4 py-3 font-medium text-right">ລູກຄ້າ</th>
                  <th className="px-4 py-3 font-medium text-right">ອໍເດີ້</th>
                  <th className="px-4 py-3 font-medium text-right">ຍອດ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.label}
                    className="border-secondary/10 border-t"
                  >
                    <td className="px-4 py-3">
                      {!isDistrict &&
                      onProvinceSelect &&
                      row.label !== "ບໍ່ລະບຸ" ? (
                        <button
                          type="button"
                          className="font-medium text-secondary hover:underline"
                          onClick={() => onProvinceSelect(row.label)}
                        >
                          {row.label}
                        </button>
                      ) : (
                        <span className="font-medium">{row.label}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.customerCount}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.orderCount}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatLak(row.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
